/**
 * Canonical contentEditable → plain-text serializer.
 *
 * The text-element edit pipeline assumes one canonical storage format:
 * a plain JS string whose only line-break is the LF character `\n`. The
 * static renderers (HTML pre-wrap, SVG tspan via getTextLines) all
 * consume that exact format.
 *
 * Browsers are inconsistent about what they put INTO a contentEditable
 * when the user presses Enter or pastes:
 *
 *   contenteditable="plaintext-only" + Chromium  → inserts a real \n
 *   contenteditable="plaintext-only" + Firefox   → not supported; falls
 *                                                   back to true CE and
 *                                                   inserts <br> / <div>
 *   true contentEditable           + any browser → <br> / <div> / <p>
 *
 * `node.textContent` flattens away <br> entirely (zero characters) and
 * does NOT insert a `\n` at block-element boundaries. So a user typing
 * "EXCLUSIVELY⏎OFFERED" in Firefox gives `<div>EXCLUSIVELY</div><div>OFFERED</div>`,
 * whose textContent is "EXCLUSIVELYOFFERED" — the exact bug being chased.
 *
 * This serializer walks the DOM tree and emits the same `\n` boundaries
 * the renderer expects, regardless of which DOM shape the browser used.
 * It is the inverse of `whiteSpace: pre-wrap` rendering.
 */

const BLOCK_TAGS = new Set([
  'DIV', 'P', 'LI', 'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE',
  'HEADER', 'FOOTER', 'ASIDE', 'NAV', 'MAIN', 'FIGURE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
]);

/**
 * Serialize a contentEditable root into plain text.
 * - Text nodes are appended verbatim (spaces preserved).
 * - <br> emits a single \n.
 * - Block-element boundaries emit a \n BEFORE the block (skipped for the
 *   first one so the output isn't prefixed with a stray \n).
 *
 * @param {Node} root - The contentEditable root node.
 * @returns {string} - Plain text with \n line-breaks. No trimming.
 */
export function serializeContentEditable(root) {
  if (!root) return '';
  // textContent fast path: if the root has no element children at all,
  // its text nodes are the entire content and no normalization is needed.
  // Avoids tree-walking for the common Chromium plaintext-only case.
  const hasElementChild = (() => {
    for (let n = root.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 1) return true;
    }
    return false;
  })();
  if (!hasElementChild) {
    return root.textContent ?? '';
  }

  let out = '';
  let seenBlock = false;

  const visit = (node) => {
    if (!node) return;
    if (node.nodeType === 3) {
      // Text node — append verbatim, no collapse.
      out += node.nodeValue || '';
      return;
    }
    if (node.nodeType !== 1) return;

    const tag = node.tagName;
    if (tag === 'BR') {
      out += '\n';
      return;
    }

    const isBlock = BLOCK_TAGS.has(tag);
    if (isBlock) {
      if (seenBlock) out += '\n';
      else seenBlock = true;
      // Chrome's empty-line idiom is <div><br></div> — a block whose ONLY
      // child is a <br>. The <br> is a layout filler so the empty block
      // has height, not a real line break. The block-boundary \n above is
      // already the line break the user typed; recursing into the <br>
      // would emit a second \n and produce N+1 visual lines for N enters.
      // Detect: block has exactly one element child, and it's a <br>.
      const only = node.firstChild;
      if (
        only &&
        only.nodeType === 1 &&
        only.tagName === 'BR' &&
        !only.nextSibling
      ) {
        return;
      }
    }
    for (let child = node.firstChild; child; child = child.nextSibling) {
      visit(child);
    }
  };

  for (let child = root.firstChild; child; child = child.nextSibling) {
    visit(child);
  }
  return out;
}

/**
 * Insert a literal `\n` text character at the current selection inside
 * `root`. Replaces selected text (if any), then collapses the caret
 * after the inserted character.
 *
 * Triggers an `input` event on `root` so consumers (auto-grow, history)
 * see the change without depending on browser keydown→input timing.
 *
 * @param {HTMLElement} root - The contentEditable element.
 * @returns {boolean} - true if a newline was inserted.
 */
export function insertNewlineAtCaret(root) {
  if (!root) return false;
  const sel = (root.ownerDocument || document).getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  // Reject carets outside this root (e.g. picked up from another editable).
  if (!root.contains(range.startContainer)) return false;

  range.deleteContents();
  const lf = (root.ownerDocument || document).createTextNode('\n');
  range.insertNode(lf);
  // Place caret AFTER the inserted \n.
  range.setStartAfter(lf);
  range.setEndAfter(lf);
  sel.removeAllRanges();
  sel.addRange(range);
  // Some browsers don't render a trailing newline unless a zero-width
  // anchor follows it. Forcing layout read keeps the caret visible at
  // the new line start.
  void root.offsetHeight;
  try {
    root.dispatchEvent(new Event('input', { bubbles: true }));
  } catch { /* old browsers */ }
  return true;
}

/**
 * Insert plain text at the caret (used by the paste handler to strip
 * any HTML the clipboard offered). Multi-line text uses real `\n`.
 *
 * @param {HTMLElement} root - The contentEditable element.
 * @param {string} text - Plain text to insert.
 * @returns {boolean} - true if text was inserted.
 */
export function insertPlainTextAtCaret(root, text) {
  if (!root || typeof text !== 'string' || text.length === 0) return false;
  const sel = (root.ownerDocument || document).getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return false;

  // Normalize CRLF / CR → LF so the inserted text matches the storage
  // format the renderer expects.
  const normalized = text.replace(/\r\n?/g, '\n');
  range.deleteContents();
  const node = (root.ownerDocument || document).createTextNode(normalized);
  range.insertNode(node);
  range.setStartAfter(node);
  range.setEndAfter(node);
  sel.removeAllRanges();
  sel.addRange(range);
  try {
    root.dispatchEvent(new Event('input', { bubbles: true }));
  } catch { /* old browsers */ }
  return true;
}
